import React, { SetStateAction, useMemo, useRef, useState } from "react";
import { IconButton, Stack, Tooltip, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import { KeyboardArrowDown, KeyboardArrowUp, Visibility, VisibilityOff } from "@mui/icons-material";
import AgentIcon from "../../components/Icons/AgentIcon";
import WeaponIcon from "../../components/Icons/WeaponIcon";
import AbilityIcon from "../../components/Icons/AbilityIcon";
import { formatDuration } from "../../utils";
import { attacker_color, defender_color, scrollable } from "../../theme";
import { KillWithAgent, OtherAbilityWithAgent, SmokeAbilityWithAgent, UtilityAbilityWithAgent } from "../../components/Maps/MapRender";
import TimestampInRoundSelect from "../../components/selectors/TimestampRangeSelect";
import { AbilityCastCard } from "../../components/Maps/Markers/AbilityCastCard";

const SMOKE_COLOR = "#8B9BB4";
const UTILITY_COLOR = "#D97706";
const GRENADE_COLOR = "#F43F5E";
const OTHER_COLOR = "#8B5CF6";
const ULTIMATE_COLOR = "#38BDF8";
const KILL_COLOR = "#DC2626";
const HEADER_WIDTH = 132;
const CLIP_HEIGHT = 36;
const KILL_CLIP_HEIGHT = 38;
const TRACK_PAD_Y = 6;
const MIN_CLIP_PCT = 3.6;
const KILL_MIN_CLIP_PCT = 5.2;
const START_AT_PCT = 0.10;
const MAX_LANES = 2;
const PACK_GAP_SECONDS = 1.2;
const KILL_LANE_STEP = 18;
const CLIP_LANE_STEP = 14;

type EventType = "KILL_EVENT" | "SMOKE_EVENT" | "UTILITY_EVENT" | "GRENADE_EVENT" | "OTHER_EVENT" | "ULTIMATE_EVENT";
type Side = "Attacker" | "Defender";

type TimelineClip = {
    type: EventType;
    data: any;
    time: number;
    duration: number;
    startPct: number;
    widthPct: number;
    lane: number;
};

const TRACKS: { id: EventType; label: string; color: string }[] = [
    { id: "KILL_EVENT", label: "Kills", color: KILL_COLOR },
    { id: "SMOKE_EVENT", label: "Smokes", color: SMOKE_COLOR },
    { id: "UTILITY_EVENT", label: "Util", color: UTILITY_COLOR },
    { id: "GRENADE_EVENT", label: "Nades", color: GRENADE_COLOR },
    { id: "OTHER_EVENT", label: "Abilities", color: OTHER_COLOR },
    { id: "ULTIMATE_EVENT", label: "Ults", color: ULTIMATE_COLOR },
];

const SIDES: { id: Side; label: string; color: string }[] = [
    { id: "Attacker", label: "ATK", color: attacker_color },
    { id: "Defender", label: "DEF", color: defender_color },
];

type TrackRow = {
    key: string;
    label: string;
    color: string;
    agent?: string;
    clips: TimelineClip[];
};

function playerDisplayName(data: any) {
    return String(data?.player || data?.agent || "Unknown").trim() || "Unknown";
}

function playerRows(clips: Omit<TimelineClip, "lane">[]): TrackRow[] {
    const grouped = new Map<string, { data: any; clips: Omit<TimelineClip, "lane">[] }>();
    clips.forEach((clip) => {
        const name = playerDisplayName(clip.data);
        const key = `${clipSide(clip.data)}|${name}`;
        const existing = grouped.get(key);
        if (existing) {
            existing.clips.push(clip);
        } else {
            grouped.set(key, { data: clip.data, clips: [clip] });
        }
    });
    return [...grouped.entries()]
        .sort((a, b) => {
            const sideA = clipSide(a[1].data);
            const sideB = clipSide(b[1].data);
            if (sideA !== sideB) return sideA === "Attacker" ? -1 : 1;
            return playerDisplayName(a[1].data).localeCompare(playerDisplayName(b[1].data));
        })
        .map(([key, group]) => ({
            key,
            label: playerDisplayName(group.data),
            color: clipSide(group.data) === "Defender" ? defender_color : attacker_color,
            agent: group.data.agent,
            clips: packTrack(group.clips.sort((a, b) => a.time - b.time)),
        }));
}

function sideRows(clips: Omit<TimelineClip, "lane">[]): TrackRow[] {
    return SIDES
        .map((side) => ({
            key: side.id,
            label: side.label,
            color: side.color,
            clips: packTrack(clips.filter((clip) => clipSide(clip.data) === side.id).sort((a, b) => a.time - b.time)),
        }))
        .filter((row) => row.clips.length > 0);
}

function getTimeInRound(event: any): number {
    if ("time_in_round" in event) return event.time_in_round ?? 0;
    if ("cast_position" in event && event.cast_position?.time_in_round !== undefined) {
        return event.cast_position.time_in_round;
    }
    return 0;
}

function clipSide(data: any): Side {
    return data?.side === "Defender" ? "Defender" : "Attacker";
}

function clipDuration(type: EventType, data: any): number {
    if (type === "SMOKE_EVENT") return Math.max(Number(data.duration) || 1.5, 1.2);
    if (type === "KILL_EVENT") return 1.5;
    return 1.5;
}

function clipHeight(type: EventType): number {
    return type === "KILL_EVENT" ? KILL_CLIP_HEIGHT : CLIP_HEIGHT;
}

function laneStep(type: EventType): number {
    return type === "KILL_EVENT" ? KILL_LANE_STEP : CLIP_LANE_STEP;
}

function packTrack(clips: Omit<TimelineClip, "lane">[]): TimelineClip[] {
    const laneEnds: number[] = [];
    return clips.map((clip) => {
        let lane = laneEnds.findIndex((endTime) => clip.time >= endTime + PACK_GAP_SECONDS);
        if (lane < 0) {
            if (laneEnds.length < MAX_LANES) {
                lane = laneEnds.length;
                laneEnds.push(clip.time);
            } else {
                lane = laneEnds.reduce((best, endTime, index) => (endTime < laneEnds[best] ? index : best), 0);
            }
        }
        laneEnds[lane] = clip.time;
        return { ...clip, lane };
    });
}

export default function RoundEventsTimeline(props: {
    smokes: SmokeAbilityWithAgent[];
    kills: KillWithAgent[];
    utils: UtilityAbilityWithAgent[];
    grenades: OtherAbilityWithAgent[];
    others: OtherAbilityWithAgent[];
    ultimates: OtherAbilityWithAgent[];
    setTimestamp: React.Dispatch<SetStateAction<number | number[]>>;
    currentTimestamp: number;
    plantTime?: number;
    roundDuration: number;
    freezeTimeEnd?: number;
}) {
    const [open, setOpen] = useState(true);
    const [enabled, setEnabled] = useState<Record<EventType, boolean>>({
        KILL_EVENT: true,
        SMOKE_EVENT: true,
        UTILITY_EVENT: true,
        GRENADE_EVENT: false,
        OTHER_EVENT: false,
        ULTIMATE_EVENT: true,
    });
    const dragging = useRef(false);

    const clockStart = 0;
    const freezeTime = Number(props.freezeTimeEnd);
    const freeze = Number.isFinite(freezeTime) ? freezeTime : 0;
    const lastEventTime = Math.max(
        0,
        ...props.kills.map(getTimeInRound),
        ...props.smokes.map((smoke) => getTimeInRound(smoke) + Math.max(Number(smoke.duration) || 0, 0)),
        ...props.utils.map(getTimeInRound),
        ...props.grenades.map(getTimeInRound),
        ...props.others.map(getTimeInRound),
        ...props.ultimates.map(getTimeInRound),
        props.plantTime ?? 0,
    );
    const snapshotEnd = Number(props.roundDuration);
    const snapshotLooksLikeRound = Number.isFinite(snapshotEnd) && snapshotEnd > freeze && snapshotEnd <= freeze + 240;
    const viewEnd = Math.max(freeze + 1, lastEventTime, snapshotLooksLikeRound ? snapshotEnd : 0);
    const startPct = START_AT_PCT * 100;
    const preSpan = Math.max(freeze - clockStart, 1);
    const postSpan = Math.max(viewEnd - freeze, 1);

    const timeToPct = (time: number) => {
        if (freeze <= clockStart) {
            return ((time - clockStart) / Math.max(viewEnd - clockStart, 1)) * 100;
        }
        if (time >= freeze) {
            return startPct + (100 - startPct) * ((time - freeze) / postSpan);
        }
        return startPct * ((time - clockStart) / preSpan);
    };
    const pctToTime = (pct: number) => {
        if (freeze <= clockStart) {
            return clockStart + (pct / 100) * Math.max(viewEnd - clockStart, 1);
        }
        if (pct >= startPct) {
            return freeze + ((pct - startPct) / (100 - startPct)) * postSpan;
        }
        return clockStart + (pct / startPct) * preSpan;
    };

    const clipsByTrack = useMemo(() => {
        const inView = (clip: Omit<TimelineClip, "lane">) =>
            clip.startPct < 100 && clip.startPct + clip.widthPct > 0;
        const source: Record<EventType, Omit<TimelineClip, "lane">[]> = {
            KILL_EVENT: props.kills.map((kill) => makeClip("KILL_EVENT", kill, timeToPct)).filter(inView),
            SMOKE_EVENT: props.smokes.map((smoke) => makeClip("SMOKE_EVENT", smoke, timeToPct)).filter(inView),
            UTILITY_EVENT: props.utils.map((util) => makeClip("UTILITY_EVENT", util, timeToPct)).filter(inView),
            GRENADE_EVENT: props.grenades.map((grenade) => makeClip("GRENADE_EVENT", grenade, timeToPct)).filter(inView),
            OTHER_EVENT: props.others.map((ability) => makeClip("OTHER_EVENT", ability, timeToPct)).filter(inView),
            ULTIMATE_EVENT: props.ultimates.map((ability) => makeClip("ULTIMATE_EVENT", ability, timeToPct)).filter(inView),
        };
        return TRACKS.reduce((acc, track) => {
            acc[track.id] = track.id === "OTHER_EVENT"
                ? playerRows(source[track.id])
                : sideRows(source[track.id]);
            return acc;
        }, {} as Record<EventType, TrackRow[]>);
    }, [props.kills, props.smokes, props.utils, props.grenades, props.others, props.ultimates, freeze, viewEnd]);

    const availableTracks = TRACKS.filter((track) => clipsByTrack[track.id].length > 0);
    const visibleTracks = availableTracks.filter((track) => enabled[track.id]);
    const playheadPct = Math.min(100, Math.max(0, timeToPct(props.currentTimestamp)));

    const seekFromClientX = (clientX: number, target: HTMLElement) => {
        const rect = target.getBoundingClientRect();
        const pct = (clientX - rect.left) / Math.max(rect.width, 1);
        const time = Math.round(pctToTime(Math.min(1, Math.max(0, pct)) * 100));
        props.setTimestamp(Math.min(viewEnd, Math.max(clockStart, time)));
    };

    const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
        dragging.current = true;
        event.currentTarget.setPointerCapture(event.pointerId);
        seekFromClientX(event.clientX, event.currentTarget);
    };

    const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
        if (!dragging.current) return;
        seekFromClientX(event.clientX, event.currentTarget);
    };

    const onPointerUp = () => {
        dragging.current = false;
    };

    const toggleTrack = (id: EventType) => {
        setEnabled((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const onSliderChange = (value: number | number[]) => {
        const display = Number(Array.isArray(value) ? value[0] : value);
        const next = Math.round(pctToTime(display));
        if (next === props.currentTimestamp) {
            const currentDisplay = timeToPct(props.currentTimestamp);
            if (display > currentDisplay) {
                props.setTimestamp(Math.min(viewEnd, props.currentTimestamp + 1));
            } else if (display < currentDisplay) {
                props.setTimestamp(Math.max(clockStart, props.currentTimestamp - 1));
            }
            return;
        }
        props.setTimestamp(Math.min(viewEnd, Math.max(clockStart, next)));
    };

    return (
        <Box
            sx={{
                width: "100%",
                pointerEvents: "auto",
                borderRadius: "10px",
                background: "rgba(10, 10, 20, 0.82)",
                border: "1px solid rgba(255,255,255,0.08)",
                overflow: "hidden",
                position: "relative",
                boxShadow: "0 12px 32px rgba(0,0,0,0.35)",
                backdropFilter: "blur(12px)",
                height: "auto",
                maxHeight: "100%",
                minHeight: 0,
                flex: "0 1 auto",
                display: "flex",
                flexDirection: "column",
            }}
        >
            <Box sx={{ position: "relative", flexShrink: 0 }}>
                <Stack direction="row" alignItems="stretch">
                    <Stack
                        spacing={0.35}
                        justifyContent="center"
                        sx={{
                            width: HEADER_WIDTH,
                            minWidth: HEADER_WIDTH,
                            px: 1,
                            py: 0.5,
                            borderRight: "1px solid rgba(255,255,255,0.06)",
                        }}
                    >
                        <Stack direction="row" spacing={0.6} alignItems="center">
                            <Typography sx={{ fontSize: "0.72rem", fontWeight: 800, fontFamily: "monospace", color: "rgba(255,255,255,0.9)" }}>
                                {formatDuration(props.currentTimestamp)}
                            </Typography>
                            <Tooltip title={open ? "Hide tracks" : "Show tracks"} arrow>
                                <IconButton
                                    size="small"
                                    onClick={() => setOpen((prev) => !prev)}
                                    sx={{
                                        p: 0.15,
                                        color: "rgba(255,255,255,0.7)",
                                        "&:hover": { color: "#fff", background: "rgba(255,255,255,0.08)" },
                                    }}
                                >
                                    {open ? <KeyboardArrowUp sx={{ fontSize: 18 }} /> : <KeyboardArrowDown sx={{ fontSize: 18 }} />}
                                </IconButton>
                            </Tooltip>
                        </Stack>
                        {open ? (
                            <Stack direction="row" spacing={0.4} flexWrap="wrap" useFlexGap>
                                {availableTracks.map((track) => (
                                    <Tooltip key={track.id} title={enabled[track.id] ? `Hide ${track.label}` : `Show ${track.label}`} arrow>
                                        <IconButton
                                            size="small"
                                            onClick={() => toggleTrack(track.id)}
                                            sx={{
                                                p: 0.25,
                                                borderRadius: "4px",
                                                color: enabled[track.id] ? track.color : "rgba(255,255,255,0.28)",
                                                background: enabled[track.id] ? `${track.color}22` : "transparent",
                                                "&:hover": { background: `${track.color}33` },
                                            }}
                                        >
                                            {enabled[track.id] ? <Visibility sx={{ fontSize: 14 }} /> : <VisibilityOff sx={{ fontSize: 14 }} />}
                                        </IconButton>
                                    </Tooltip>
                                ))}
                            </Stack>
                        ) : null}
                    </Stack>
                    <Box sx={{ flex: 1, minWidth: 0, pr: 1, pl: 1 }}>
                        <TimestampInRoundSelect
                            value={timeToPct(props.currentTimestamp)}
                            onChange={onSliderChange}
                            min={0}
                            roundDuration={100}
                            freeze_time_end={startPct}
                            spike_time={props.plantTime !== undefined ? timeToPct(props.plantTime) : undefined}
                            valueLabelFormat={(value) => formatDuration(pctToTime(Number(value)))}
                            sx={{ width: "100%", py: 0, padding: "2px 0 0", background: "transparent", border: "none", "&::before": { display: "none" }, "&::after": { display: "none" } }}
                        />
                    </Box>
                </Stack>
            </Box>

            {open ? (
                <Box
                    sx={{
                        position: "relative",
                        flex: 1,
                        minHeight: 0,
                        overscrollBehavior: "contain",
                        scrollbarWidth: "thin",
                        scrollbarColor: "rgba(255,255,255,0.35) transparent",
                        ...scrollable,
                        overflowX: "hidden",
                        overflowY: "auto",
                    }}
                >
                    {visibleTracks.map((track) => {
                        const height = clipHeight(track.id);
                        const step = laneStep(track.id);
                        const rows = clipsByTrack[track.id];
                        const perPlayer = track.id === "OTHER_EVENT";
                        return (
                            <React.Fragment key={track.id}>
                                {perPlayer ? (
                                    <Stack direction="row" alignItems="center" sx={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                                        <Typography sx={{
                                            width: HEADER_WIDTH,
                                            minWidth: HEADER_WIDTH,
                                            px: 1,
                                            py: 0.35,
                                            fontSize: "0.58rem",
                                            fontWeight: 800,
                                            letterSpacing: "0.1em",
                                            textTransform: "uppercase",
                                            color: track.color,
                                            borderRight: "1px solid rgba(255,255,255,0.06)",
                                            borderLeft: `3px solid ${track.color}`,
                                            background: `${track.color}14`,
                                        }}>
                                            {track.label}
                                        </Typography>
                                        <Box sx={{ flex: 1 }} />
                                    </Stack>
                                ) : null}
                                {rows.map((row, rowIndex) => {
                            const laneCount = Math.max(1, row.clips.reduce((max, clip) => Math.max(max, clip.lane + 1), 0));
                            return (
                                <Stack
                                    key={`${track.id}-${row.key}`}
                                    direction="row"
                                    alignItems="stretch"
                                    sx={{
                                        borderTop: perPlayer || rowIndex > 0 ? "1px solid rgba(255,255,255,0.03)" : "1px solid rgba(255,255,255,0.08)",
                                    }}
                                >
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        spacing={0.6}
                                        sx={{
                                            width: HEADER_WIDTH,
                                            minWidth: HEADER_WIDTH,
                                            px: 1,
                                            borderRight: "1px solid rgba(255,255,255,0.06)",
                                            background: `linear-gradient(90deg, ${row.color}24 0%, ${track.color}10 100%)`,
                                            borderLeft: `3px solid ${row.color}`,
                                        }}
                                    >
                                        {perPlayer && row.agent ? (
                                            <AgentIcon disableTooltip agent_name={row.agent} sx={{ width: 18, height: 18, minWidth: 18, minHeight: 18 }} />
                                        ) : null}
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            {perPlayer ? null : (
                                                <Typography sx={{ fontSize: "0.62rem", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: "rgba(255,255,255,0.55)", lineHeight: 1 }}>
                                                    {track.label}
                                                </Typography>
                                            )}
                                            <Typography sx={{
                                                fontSize: perPlayer ? "0.62rem" : "0.68rem",
                                                fontWeight: 800,
                                                letterSpacing: "0.04em",
                                                color: row.color,
                                                lineHeight: 1.2,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}>
                                                {row.label}
                                            </Typography>
                                        </Box>
                                        <Typography sx={{ fontSize: "0.58rem", color: "rgba(255,255,255,0.35)", ml: "auto" }}>
                                            {row.clips.length}
                                        </Typography>
                                    </Stack>
                                    <Box
                                        onPointerDown={onPointerDown}
                                        onPointerMove={onPointerMove}
                                        onPointerUp={onPointerUp}
                                        onPointerCancel={onPointerUp}
                                        sx={{
                                            flex: 1,
                                            minWidth: 0,
                                            position: "relative",
                                            height: TRACK_PAD_Y * 2 + height + Math.max(0, laneCount - 1) * step,
                                            mx: 1,
                                            cursor: "ew-resize",
                                            background: "repeating-linear-gradient(90deg, rgba(255,255,255,0.02) 0, rgba(255,255,255,0.02) 1px, transparent 1px, transparent 10%)",
                                        }}
                                    >
                                        {row.clips.map((clip, index) => (
                                            <TrackClip
                                                key={`${track.id}-${row.key}-${index}`}
                                                clip={clip}
                                                color={track.color}
                                                height={height}
                                                laneStep={step}
                                                isActive={props.currentTimestamp >= clip.time && props.currentTimestamp <= clip.time + clip.duration}
                                            />
                                        ))}
                                    </Box>
                                </Stack>
                            );
                                })}
                            </React.Fragment>
                        );
                    })}
                </Box>
            ) : null}

            {open ? (
                <Box
                    sx={{
                        position: "absolute",
                        left: HEADER_WIDTH + 8,
                        right: 8,
                        top: 0,
                        bottom: 0,
                        pointerEvents: "none",
                        zIndex: 8,
                    }}
                >
                    <Box
                        sx={{
                            position: "absolute",
                            top: 6,
                            bottom: 0,
                            left: `${playheadPct}%`,
                            width: "2px",
                            background: "#fff",
                            boxShadow: "0 0 10px rgba(255,255,255,0.7)",
                            transform: "translateX(-50%)",
                            "&::before": {
                                content: '""',
                                position: "absolute",
                                top: 0,
                                left: "50%",
                                transform: "translate(-50%, -10%)",
                                width: 0,
                                height: 0,
                                borderLeft: "6px solid transparent",
                                borderRight: "6px solid transparent",
                                borderTop: "8px solid #fff",
                            },
                        }}
                    />
                </Box>
            ) : null}
        </Box>
    );
}

function makeClip(type: EventType, data: any, timeToPct: (time: number) => number): Omit<TimelineClip, "lane"> {
    const time = getTimeInRound(data);
    const duration = clipDuration(type, data);
    const startPct = timeToPct(time);
    const minPct = type === "KILL_EVENT" ? KILL_MIN_CLIP_PCT : MIN_CLIP_PCT;
    const widthPct = Math.max(timeToPct(time + duration) - startPct, minPct);
    return { type, data, time, duration, startPct, widthPct };
}

function TrackClip(props: { clip: TimelineClip; color: string; height: number; laneStep: number; isActive?: boolean }) {
    const { clip, color, height, laneStep, isActive } = props;
    const side = clip.data.side as string | undefined;
    const sideColor = side === "Attacker" ? attacker_color : side === "Defender" ? defender_color : color;
    const isKill = clip.type === "KILL_EVENT";
    const isAbility = clip.type === "UTILITY_EVENT" || clip.type === "GRENADE_EVENT" || clip.type === "OTHER_EVENT" || clip.type === "ULTIMATE_EVENT";

    let title: React.ReactNode = formatDuration(clip.time);
    let body: React.ReactNode = null;

    switch (clip.type) {
        case "KILL_EVENT": {
            const kill = clip.data as KillWithAgent;
            const weapon = kill.killer_weapon ? kill.killer_weapon.replace(/_/g, " ") : "";
            title = `${formatDuration(clip.time)} · ${kill.killer_agent} [${weapon || kill.killer_ability || "?"}] ${kill.victim_agent}${kill.is_headshot ? " (HS)" : ""}${kill.is_first_blood ? " · FB" : ""}`;
            body = (
                <Stack direction="row" alignItems="center" spacing={0.4} sx={{ width: "100%", minWidth: 0 }}>
                    <AgentIcon disableTooltip agent_name={kill.killer_agent} sx={{ width: 28, height: 28, minWidth: 28, minHeight: 28 }} />
                    <Box
                        className="kill-weapon"
                        sx={{
                            maxWidth: 0,
                            opacity: 0,
                            overflow: "hidden",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.4,
                            transition: "max-width 0.16s ease, opacity 0.12s ease, margin 0.16s ease",
                        }}
                    >
                        {kill.killer_weapon ? (
                            <WeaponIcon weapon={kill.killer_weapon} width={72} height={24} />
                        ) : null}
                        {kill.killer_ability ? (
                            <AbilityIcon disableTooltip agent_name={kill.killer_agent} slot={kill.killer_ability} sx={{ width: 20, height: 20 }} />
                        ) : null}
                    </Box>
                    <AgentIcon disableTooltip agent_name={kill.victim_agent} sx={{ width: 28, height: 28, minWidth: 28, minHeight: 28, filter: "grayscale(100%)", opacity: 0.75 }} />
                </Stack>
            );
            break;
        }
        case "SMOKE_EVENT": {
            const smoke = clip.data as SmokeAbilityWithAgent;
            title = (
                <AbilityCastCard
                    agent={smoke.agent}
                    slot={smoke.slot}
                    side={smoke.side}
                    kind="Smoke"
                    timeLabel={formatDuration(clip.time)}
                    durationLabel={`${Math.round(smoke.duration)}s`}
                />
            );
            body = (
                <Stack direction="row" alignItems="center" spacing={0.4} sx={{ minWidth: 0, overflow: "hidden" }}>
                    <AgentIcon disableTooltip agent_name={smoke.agent} sx={{ width: 22, height: 22, minWidth: 22, minHeight: 22 }} />
                    <AbilityIcon disableTooltip agent_name={smoke.agent} slot={smoke.slot} sx={{ width: 20, height: 20 }} />
                    <Typography sx={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.75)", whiteSpace: "nowrap" }}>
                        {Math.round(smoke.duration)}s
                    </Typography>
                </Stack>
            );
            break;
        }
        case "UTILITY_EVENT": {
            const util = clip.data as UtilityAbilityWithAgent;
            title = (
                <AbilityCastCard
                    agent={util.agent}
                    slot={util.slot}
                    side={util.side}
                    kind="Utility"
                    timeLabel={formatDuration(clip.time)}
                />
            );
            body = (
                <Stack direction="row" alignItems="center" spacing={0.4} sx={{ minWidth: 0, overflow: "hidden" }}>
                    <AgentIcon disableTooltip agent_name={util.agent} sx={{ width: 26, height: 26, minWidth: 26, minHeight: 26 }} />
                    <AbilityIcon disableTooltip agent_name={util.agent} slot={util.slot} sx={{ width: 26, height: 26 }} />
                </Stack>
            );
            break;
        }
        case "GRENADE_EVENT": {
            const grenade = clip.data as OtherAbilityWithAgent;
            title = (
                <AbilityCastCard
                    agent={grenade.agent}
                    slot={grenade.slot}
                    side={grenade.side}
                    kind="Grenade"
                    timeLabel={formatDuration(clip.time)}
                />
            );
            body = (
                <Stack direction="row" alignItems="center" spacing={0.4} sx={{ minWidth: 0, overflow: "hidden" }}>
                    <AgentIcon disableTooltip agent_name={grenade.agent} sx={{ width: 26, height: 26, minWidth: 26, minHeight: 26 }} />
                    <AbilityIcon disableTooltip agent_name={grenade.agent} slot={grenade.slot} sx={{ width: 26, height: 26 }} />
                </Stack>
            );
            break;
        }
        case "OTHER_EVENT": {
            const ability = clip.data as OtherAbilityWithAgent;
            title = (
                <AbilityCastCard
                    agent={ability.agent}
                    slot={ability.slot}
                    side={ability.side}
                    kind="Ability"
                    timeLabel={formatDuration(clip.time)}
                />
            );
            body = (
                <Stack direction="row" alignItems="center" spacing={0.4} sx={{ minWidth: 0, overflow: "hidden" }}>
                    <AbilityIcon disableTooltip agent_name={ability.agent} slot={ability.slot} sx={{ width: 22, height: 22 }} />
                </Stack>
            );
            break;
        }
        case "ULTIMATE_EVENT": {
            const ability = clip.data as OtherAbilityWithAgent;
            title = (
                <AbilityCastCard
                    agent={ability.agent}
                    slot={ability.slot}
                    side={ability.side}
                    kind="Ultimate"
                    timeLabel={formatDuration(clip.time)}
                />
            );
            body = (
                <Stack direction="row" alignItems="center" spacing={0.4} sx={{ minWidth: 0, overflow: "hidden" }}>
                    <AgentIcon disableTooltip agent_name={ability.agent} sx={{ width: 26, height: 26, minWidth: 26, minHeight: 26 }} />
                    <AbilityIcon disableTooltip agent_name={ability.agent} slot={ability.slot} sx={{ width: 26, height: 26 }} />
                </Stack>
            );
            break;
        }
    }

    return (
        <Tooltip
            title={title || ""}
            arrow
            disableInteractive
            enterDelay={80}
            enterNextDelay={80}
            componentsProps={{
                tooltip: {
                    sx: {
                        bgcolor: "transparent",
                        p: 0,
                        maxWidth: "none",
                        boxShadow: "none",
                    },
                },
                arrow: { sx: { color: "rgba(6, 8, 14, 0.97)" } },
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    left: `${clip.startPct}%`,
                    width: `${clip.widthPct}%`,
                    minWidth: isKill ? 80 : isAbility ? 64 : 36,
                    top: TRACK_PAD_Y + clip.lane * laneStep,
                    height,
                    borderRadius: "6px",
                    display: "flex",
                    alignItems: "center",
                    px: isKill ? 0.6 : 0.6,
                    overflow: "hidden",
                    cursor: "pointer",
                    pointerEvents: "auto",
                    background: isKill
                        ? "linear-gradient(90deg, rgba(12,12,18,0.95) 0%, rgba(40,12,12,0.92) 100%)"
                        : `linear-gradient(90deg, ${color}aa 0%, ${color}66 100%)`,
                    border: `1px solid ${isActive ? "#fff" : isKill ? "rgba(255,255,255,0.18)" : `${color}cc`}`,
                    borderLeft: `3px solid ${sideColor}`,
                    boxShadow: isActive
                        ? `0 0 10px ${color}`
                        : "0 2px 8px rgba(0,0,0,0.45), inset 0 0 0 1px rgba(255,255,255,0.06)",
                    opacity: isActive ? 1 : 0.92,
                    zIndex: isActive ? 20 : 2 + clip.lane,
                    transition: "min-width 0.16s ease, filter 0.12s ease",
                    "&:hover": {
                        filter: "brightness(1.15)",
                        zIndex: 21,
                        ...(isKill ? {
                            minWidth: 156,
                            overflow: "visible",
                            "& .kill-weapon": {
                                maxWidth: 96,
                                opacity: 1,
                                mx: 0.5,
                            },
                        } : {}),
                    },
                }}
            >
                {body}
            </Box>
        </Tooltip>
    );
}
